import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, Col, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { isNil } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WebPageBlockType } from '../../../../api/types';
import SingleUploadField from '../../../../components/fields/SingleUploadField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../../../utilities/extensions';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import colSpan from '../../../shared/ApplicationDetailsPage/standard/colSpan';
import { type ImageBlockInput, type WebpageFormikValue } from '../shared';
import ImageDescriptionBlock from './ImageDescriptionBlock';
import ReorderingButtons, { getNewBlock } from './shared';

type ImageBlockProps = {
    name: string;
    index: number;
    disabled?: boolean;
};

const ImageBlock = ({ name, index, disabled = false }: ImageBlockProps) => {
    const { t } = useTranslation(['webpageDetails']);
    const { values, setFieldValue } = useFormikContext<WebpageFormikValue>();
    const { yesNoSwitch } = useSystemSwitchData();
    const onSelect = useCallback(
        (value, options) => {
            values.webPageBlock[index] = getNewBlock(value);
            setFieldValue(name, getNewBlock(value));
        },
        [index, name, setFieldValue, values.webPageBlock]
    );

    const imageBlock: ImageBlockInput = useMemo(
        () =>
            values.webPageBlock[index].type === WebPageBlockType.Image &&
            (values.webPageBlock[index] as ImageBlockInput),
        [index, values.webPageBlock]
    );

    const addImageDescriptions = useCallback(() => {
        if (imageBlock.type !== WebPageBlockType.Image) {
            throw new Error('WebPageBlockType is not same as input');
        }
        setFieldValue(`${name}.descriptions`, [
            ...imageBlock.descriptions,
            {
                title: { defaultValue: '', overrides: [] },
                description: { defaultValue: '', overrides: [] },
            },
        ]);
    }, [imageBlock.type, imageBlock.descriptions, setFieldValue, name]);

    const onChange = useCallback(() => {
        setFieldValue(`${name}.imageBlockInput.button`, {
            text: { defaultValue: '', overrides: [] },
            url: '',
        });
    }, [name, setFieldValue]);

    const { webpageBlockType } = useSystemOptions();

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('webpageDetails:fields.image.blockName')}>
                <Panel
                    key={t('webpageDetails:fields.image.blockName')}
                    extra={
                        !disabled && <ReorderingButtons index={index} setFieldValue={setFieldValue} values={values} />
                    }
                    header={t('webpageDetails:fields.image.blockName')}
                >
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('webpageDetails:fields.blockType', { returnObjects: true })}
                                disabled={disabled}
                                name={`${name}.type`}
                                onSelect={onSelect}
                                options={webpageBlockType}
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <SingleUploadField
                                {...t('webpageDetails:fields.bannerImage', { returnObjects: true })}
                                disabled={disabled}
                                extensions={[...allowedExtensions.image, ...allowedExtensions.video]}
                                name={`${name}.image`}
                                sizeLimitInMiB={20}
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('webpageDetails:fields.image.title', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name={`${name}.title`}
                            />
                        </Col>
                    </Row>
                    <ImageDescriptionBlock disabled={disabled} name={`${name}.descriptions`} />
                    {!disabled && (
                        <Button icon={<PlusCircleOutlined />} onClick={addImageDescriptions} type="link">
                            {t('webpageDetails:actions.addImages')}
                        </Button>
                    )}
                    <Row gutter={14} style={{ marginTop: '10px' }}>
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('webpageDetails:fields.image.button.isActive', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name={`${name}.button.isActive`}
                                {...yesNoSwitch}
                                onClick={onChange}
                            />
                        </Col>
                        {!isNil(imageBlock?.button.isActive) && imageBlock?.button.isActive && (
                            <>
                                <Col {...colSpan}>
                                    <FormFields.TranslatedInputField
                                        {...t('webpageDetails:fields.image.button.text', {
                                            returnObjects: true,
                                        })}
                                        disabled={disabled}
                                        name={`${name}.button.text`}
                                    />
                                </Col>
                                <Col {...colSpan}>
                                    <FormFields.InputField
                                        {...t('webpageDetails:fields.image.button.url', {
                                            returnObjects: true,
                                        })}
                                        disabled={disabled}
                                        name={`${name}.button.url`}
                                    />
                                </Col>
                            </>
                        )}
                    </Row>
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default ImageBlock;
